import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { inspectionsApi } from '../lib/api';
import { formatDate, getInspectionStatusColor, getInspectionStatusLabel, calculateProgress } from '../lib/utils';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Progress } from '../components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Plus, 
  Search, 
  FileCheck,
  AlertTriangle,
  Loader2,
  Trash2
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

export default function Inspections() {
  const [inspections, setInspections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [deleteId, setDeleteId] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchInspections();
  }, []);

  const fetchInspections = async () => {
    try {
      const response = await inspectionsApi.getAll();
      setInspections(response.data);
    } catch (error) {
      console.error('Error fetching inspections:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    
    try {
      await inspectionsApi.delete(deleteId);
      setInspections(inspections.filter(i => i.id !== deleteId));
    } catch (error) {
      console.error('Error deleting inspection:', error);
    } finally {
      setDeleteId(null);
    }
  };

  const filteredInspections = inspections.filter(inspection => {
    const matchesSearch = 
      inspection.property_address.toLowerCase().includes(searchTerm.toLowerCase()) ||
      inspection.property_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      inspection.client_name.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || inspection.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 animate-in fade-in slide-in-from-bottom duration-500" data-testid="inspections-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading font-bold text-3xl md:text-4xl">Abnahmen</h1>
            <p className="text-muted-foreground mt-1">Verwalten Sie Ihre Brandschutz-Abnahmen</p>
          </div>
          <Link to="/inspections/new">
            <Button className="h-12 px-6 gap-2" data-testid="new-inspection-button">
              <Plus className="h-5 w-5" />
              Neue Abnahme
            </Button>
          </Link>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Suchen..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 h-12"
              data-testid="search-input"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full sm:w-48 h-12" data-testid="status-filter">
              <SelectValue placeholder="Status filtern" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Status</SelectItem>
              <SelectItem value="draft">Entwurf</SelectItem>
              <SelectItem value="in_progress">In Bearbeitung</SelectItem>
              <SelectItem value="completed">Abgeschlossen</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Inspections List */}
        {filteredInspections.length > 0 ? (
          <div className="space-y-4">
            {filteredInspections.map((inspection) => {
              const progress = calculateProgress(inspection.items);
              const failCount = inspection.items?.filter(i => i.status === 'fail').length || 0;
              const passCount = inspection.items?.filter(i => i.status === 'pass').length || 0;
              
              return (
                <Card 
                  key={inspection.id} 
                  className="border-border/50 card-hover cursor-pointer"
                  onClick={() => navigate(`/inspections/${inspection.id}`)}
                  data-testid={`inspection-card-${inspection.id}`}
                >
                  <CardContent className="p-6">
                    <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-heading font-semibold text-lg truncate">
                            {inspection.property_name || inspection.property_address}
                          </h3>
                          <span className={`shrink-0 inline-flex px-2 py-1 rounded-full text-xs font-medium border ${getInspectionStatusColor(inspection.status)}`}>
                            {getInspectionStatusLabel(inspection.status)}
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
                          <span>{inspection.client_name}</span>
                          <span>•</span>
                          <span>{inspection.building_type}</span>
                          <span>•</span>
                          <span>{formatDate(inspection.created_at)}</span>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-6">
                        {/* Stats */}
                        <div className="flex items-center gap-4 text-sm">
                          <div className="flex items-center gap-1.5">
                            <div className="h-2 w-2 rounded-full bg-green-500" />
                            <span className="text-muted-foreground">{passCount} OK</span>
                          </div>
                          {failCount > 0 && (
                            <div className="flex items-center gap-1.5 text-red-500">
                              <AlertTriangle className="h-4 w-4" />
                              <span>{failCount} Mängel</span>
                            </div>
                          )}
                        </div>
                        
                        {/* Progress */}
                        <div className="hidden sm:block w-32">
                          <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                            <span>Fortschritt</span>
                            <span>{progress}%</span>
                          </div>
                          <Progress value={progress} className="h-1.5" />
                        </div>
                        
                        {/* Delete Button */}
                        <Button
                          variant="ghost"
                          size="icon"
                          className="shrink-0 text-muted-foreground hover:text-red-500"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteId(inspection.id);
                          }}
                          data-testid={`delete-inspection-${inspection.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ) : (
          <Card className="border-border/50">
            <CardContent className="py-16">
              <div className="text-center">
                <FileCheck className="h-12 w-12 text-muted-foreground mx-auto mb-4" strokeWidth={1} />
                <p className="text-muted-foreground mb-4">
                  {searchTerm || statusFilter !== 'all' 
                    ? 'Keine Abnahmen gefunden' 
                    : 'Noch keine Abnahmen vorhanden'}
                </p>
                <Link to="/inspections/new">
                  <Button variant="outline" className="gap-2">
                    <Plus className="h-4 w-4" />
                    Neue Abnahme erstellen
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Abnahme löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              Diese Aktion kann nicht rückgängig gemacht werden. Alle Daten dieser Abnahme werden dauerhaft gelöscht.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600">
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}
